import { useEffect, useRef, useState } from "react"
import type { TestChatState } from "@/entities/bot-builder"

/** Дольше этого тест-чат сам не ждёт: длинную паузу проверяют кнопкой «Пропустить». */
const AUTO_WAKE_LIMIT_MS = 10 * 60_000

/**
 * Пауза в тест-чате идёт как в Telegram: когда подходит время, бот продолжает сам.
 * Возвращает, сколько миллисекунд осталось, или null, если бот ничего не ждёт.
 * Одну и ту же паузу будит не больше одного раза, чтобы ошибка сервера не зациклила запросы.
 */
export function usePauseClock(
	pending: TestChatState["pendingWakeup"] | undefined,
	isBusy: boolean,
	wake: () => void,
): { remainingMs: number; auto: boolean } | null {
	const key = pending ? `${pending.nodeId}:${pending.epoch}:${pending.runAt}` : null
	const runAt = pending ? Date.parse(pending.runAt) : Number.NaN
	const [now, setNow] = useState(() => Date.now())
	const woken = useRef<string | null>(null)
	const wakeRef = useRef(wake)
	wakeRef.current = wake

	useEffect(() => {
		if (!key) return
		setNow(Date.now())
		const tick = setInterval(() => setNow(Date.now()), 250)
		return () => clearInterval(tick)
	}, [key])

	useEffect(() => {
		if (!key || Number.isNaN(runAt) || isBusy || woken.current === key) return
		const delay = runAt - Date.now()
		if (delay > AUTO_WAKE_LIMIT_MS) return
		const timer = setTimeout(
			() => {
				woken.current = key
				wakeRef.current()
			},
			Math.max(0, delay),
		)
		return () => clearTimeout(timer)
	}, [key, runAt, isBusy])

	if (!key || Number.isNaN(runAt)) return null
	return { remainingMs: Math.max(0, runAt - now), auto: runAt - now <= AUTO_WAKE_LIMIT_MS }
}
